import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { lightPalette } from '../palette'
import { Eyebrow } from './Eyebrow'

const rowGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))',
  gap: 52,
  alignItems: 'center',
} as const

const rowTitleStyle = {
  margin: 0,
  fontSize: 'clamp(25px,2.6vw,33px)',
  lineHeight: 1.15,
  fontWeight: 600,
  letterSpacing: '-0.03em',
} as const

const rowDescStyle = {
  margin: 0,
  fontSize: 16,
  lineHeight: 1.65,
  color: lightPalette.textBody,
} as const

const mockCardStyle = {
  border: `1px solid ${lightPalette.borderDefault}`,
  borderRadius: 16,
  background: lightPalette.surfaceSubtle,
  padding: 20,
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  boxShadow: '0 20px 50px oklch(20% 0 0 / 0.07)',
} as const

function SuggestionRow() {
  const { t } = useTranslation()

  return (
    <div style={rowGridStyle}>
      <div data-reveal="" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Eyebrow>{t('landing.featureSuggestion.eyebrow')}</Eyebrow>
        <h3 style={rowTitleStyle}>{t('landing.featureSuggestion.title')}</h3>
        <p style={rowDescStyle}>{t('landing.featureSuggestion.desc')}</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 9, marginTop: 4 }}>
          {[
            t('landing.featureSuggestion.bullet1'),
            t('landing.featureSuggestion.bullet2'),
            t('landing.featureSuggestion.bullet3'),
          ].map((bullet) => (
            <span
              key={bullet}
              style={{ display: 'flex', gap: 10, fontSize: 14.5, color: 'oklch(38% 0 0)' }}
            >
              <span style={{ color: lightPalette.successFg }} aria-hidden="true">
                ✓
              </span>
              {bullet}
            </span>
          ))}
        </div>
      </div>

      <div data-reveal="" style={mockCardStyle}>
        <div
          style={{
            border: `1px solid ${lightPalette.purpleBorder}`,
            background: lightPalette.purpleBg,
            borderRadius: 12,
            padding: '14px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              color: lightPalette.purpleFg,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
            }}
          >
            {t('landing.featureSuggestion.mockLabel')}
          </span>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55 }}>
            {t('landing.featureSuggestion.mockText')}
          </p>
        </div>
        <div
          style={{
            border: `1px solid ${lightPalette.borderDefault}`,
            borderRadius: 11,
            background: lightPalette.surface,
            padding: '12px 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 5,
          }}
        >
          <span style={{ fontSize: 12.5, fontWeight: 700 }}>
            {t('landing.featureSuggestion.sourceCardTitle')}
          </span>
          <span style={{ fontSize: 12, color: lightPalette.textMuted, lineHeight: 1.5 }}>
            {t('landing.featureSuggestion.sourceCardQuote')}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 9,
            padding: '10px 12px',
            border: `1px solid ${lightPalette.borderDefault}`,
            borderRadius: 10,
            background: lightPalette.surface,
          }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: 5,
              background: lightPalette.successBg,
              color: lightPalette.successFg,
            }}
          >
            {t('landing.featureSuggestion.toolBadge')}
          </span>
          <code style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11.5 }}>
            {t('landing.featureSuggestion.toolCode')}
          </code>
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 11.5,
              fontWeight: 600,
              color: lightPalette.successFg,
            }}
          >
            {t('landing.featureSuggestion.toolLatency')}
          </span>
        </div>
      </div>
    </div>
  )
}

function KnowledgeRow() {
  const { t } = useTranslation()

  const coverageRows = [
    {
      label: t('landing.featureKnowledge.row1Label'),
      status: t('landing.featureKnowledge.row1Status'),
      tone: lightPalette.successFg,
    },
    {
      label: t('landing.featureKnowledge.row2Label'),
      status: t('landing.featureKnowledge.row2Status'),
      tone: lightPalette.successFg,
    },
    {
      label: t('landing.featureKnowledge.row3Label'),
      status: t('landing.featureKnowledge.row3Status'),
      tone: lightPalette.warningFg,
    },
  ]

  return (
    <div style={rowGridStyle}>
      <div data-reveal="" style={{ order: 2, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Eyebrow>{t('landing.featureKnowledge.eyebrow')}</Eyebrow>
        <h3 style={rowTitleStyle}>{t('landing.featureKnowledge.title')}</h3>
        <p style={rowDescStyle}>{t('landing.featureKnowledge.desc')}</p>
        <Link
          to="/signup"
          style={{
            alignSelf: 'flex-start',
            fontSize: 14.5,
            fontWeight: 700,
            marginTop: 4,
            color: lightPalette.brandAccent,
          }}
        >
          {t('landing.featureKnowledge.link')}
        </Link>
      </div>

      <div
        data-reveal=""
        style={{
          border: `1px solid ${lightPalette.borderDefault}`,
          borderRadius: 16,
          background: lightPalette.surfaceSubtle,
          overflow: 'hidden',
          boxShadow: '0 20px 50px oklch(20% 0 0 / 0.07)',
        }}
      >
        {coverageRows.map((row) => (
          <div
            key={row.label}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '13px 18px',
              borderBottom: `1px solid ${lightPalette.borderSofter}`,
              fontSize: 13.5,
              background: lightPalette.surface,
            }}
          >
            <span>{row.label}</span>
            <span style={{ fontSize: 11.5, fontWeight: 700, color: row.tone }}>{row.status}</span>
          </div>
        ))}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '15px 18px',
            fontSize: 13.5,
            background: lightPalette.dangerRowBg,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <span style={{ fontWeight: 600 }}>{t('landing.featureKnowledge.row4Label')}</span>
            <span style={{ fontSize: 12, color: lightPalette.textMuted }}>
              {t('landing.featureKnowledge.row4Meta')}
            </span>
          </div>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              padding: '6px 13px',
              borderRadius: 100,
              background: lightPalette.ink,
              color: '#fff',
            }}
          >
            {t('landing.featureKnowledge.row4Action')}
          </span>
        </div>
      </div>
    </div>
  )
}

function ActionsRow() {
  const { t } = useTranslation()

  const tools = [
    {
      badge: t('landing.featureActions.tool1Badge'),
      code: t('landing.featureActions.tool1Code'),
      status: t('landing.featureActions.tool1Status'),
      badgeBg: lightPalette.successBg,
      badgeFg: lightPalette.successFg,
      opacity: 1,
    },
    {
      badge: t('landing.featureActions.tool2Badge'),
      code: t('landing.featureActions.tool2Code'),
      status: t('landing.featureActions.tool2Status'),
      badgeBg: lightPalette.warningBg,
      badgeFg: lightPalette.warningFg,
      opacity: 1,
    },
    {
      badge: t('landing.featureActions.tool3Badge'),
      code: t('landing.featureActions.tool3Code'),
      status: t('landing.featureActions.tool3Status'),
      badgeBg: lightPalette.dangerBg,
      badgeFg: lightPalette.dangerFg,
      opacity: 0.62,
    },
  ]

  return (
    <div style={rowGridStyle}>
      <div data-reveal="" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Eyebrow>{t('landing.featureActions.eyebrow')}</Eyebrow>
        <h3 style={rowTitleStyle}>{t('landing.featureActions.title')}</h3>
        <p style={rowDescStyle}>{t('landing.featureActions.desc')}</p>
        <Link
          to="/signup"
          style={{
            alignSelf: 'flex-start',
            fontSize: 14.5,
            fontWeight: 700,
            marginTop: 4,
            color: lightPalette.brandAccent,
          }}
        >
          {t('landing.featureActions.link')}
        </Link>
      </div>

      <div data-reveal="" style={{ display: 'flex', flexDirection: 'column', gap: 11 }}>
        {tools.map((tool) => (
          <div
            key={tool.code}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '15px 17px',
              border: `1px solid ${lightPalette.borderDefault}`,
              borderRadius: 12,
              background: lightPalette.surfaceSubtle,
              opacity: tool.opacity,
            }}
          >
            <span
              style={{
                fontSize: 10.5,
                fontWeight: 700,
                padding: '3px 9px',
                borderRadius: 6,
                background: tool.badgeBg,
                color: tool.badgeFg,
              }}
            >
              {tool.badge}
            </span>
            <code style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 12 }}>
              {tool.code}
            </code>
            <span style={{ marginLeft: 'auto', fontSize: 12, color: lightPalette.textMuted }}>
              {tool.status}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function FeatureRows() {
  return (
    <section
      style={{
        background: lightPalette.surface,
        borderTop: `1px solid ${lightPalette.borderSoft}`,
        borderBottom: `1px solid ${lightPalette.borderSoft}`,
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          padding: '88px 24px',
          display: 'flex',
          flexDirection: 'column',
          gap: 80,
        }}
      >
        <SuggestionRow />
        <KnowledgeRow />
        <ActionsRow />
      </div>
    </section>
  )
}
